import { z } from "zod";
import { CareActionLifecycleStatusSchema } from "./care-bundle.js";
import { CopilotEngineSchema, PlanActionSchema, SuggestedAssessmentSchema, SuggestedDiagnosisCodeSchema, } from "./encounter-documentation.js";
import { CareValidationCheckSchema, UncertaintySchema } from "./foundation.js";
import { IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { VisitContextRefSchema } from "./visit-context.js";
/**
 * The encounter Care Bundle: the plan the copilot synthesized, projected onto
 * the foundation's validated-action vocabulary.
 *
 * It extends the typed plan action the product already reviews
 * (`PlanActionSchema`) with what the foundation specification requires of
 * a reviewable bundle — a stable action identity, a lifecycle status,
 * dependencies, deterministic validation results, and explicit uncertainty —
 * using the foundation's own check and uncertainty schemas rather than a
 * second vocabulary. Trusted identity (tenant, patient, encounter, context
 * version, generation) is bound by the application, never supplied by the
 * model, and nothing in this contract can express approval or signature.
 */
export const ENCOUNTER_CARE_BUNDLE_SCHEMA_VERSION = "0.1.0";
export const EncounterActionIdSchema = z.string().regex(/^act_[a-f0-9]{16}$/);
export const EncounterBundleIdSchema = z.string().regex(/^bundle_[a-f0-9]{16}$/);
/** Where in the encounter record the action's key terms are documented. */
export const EncounterSourceSectionSchema = z.enum([
    "chief_complaint",
    "subjective",
    "objective",
    "assessment",
    "plan",
    "dictation",
    "clinician_intent",
]);
export const EncounterLabTestResolutionSchema = z
    .object({
    display: z.string().min(1).max(240),
    code: z.string().min(1).max(80),
    codeSystem: z.enum(["LOINC", "local", "free_text"]),
    /** Catalogue key when the test resolved against the trusted laboratory catalogue. */
    catalogueKey: z.string().min(1).max(120).nullable(),
    loinc: z.string().min(1).max(40).nullable(),
    resolved: z.boolean(),
})
    .strict();
export const EncounterActionResolutionSchema = z
    .object({
    labTests: z.array(EncounterLabTestResolutionSchema).max(40).optional(),
    specialty: z
        .object({
        code: z.string().min(1).max(80),
        displayName: z.string().min(1).max(160),
        catalogVersion: z.string().min(1).max(80),
    })
        .strict()
        .nullable()
        .optional(),
})
    .strict();
export const EncounterActionValidationStatusSchema = z.enum([
    "not_run",
    "pass",
    "pass_with_warnings",
    "fail",
]);
export const EncounterActionValidationSchema = z
    .object({
    status: EncounterActionValidationStatusSchema,
    /** True when at least one failed check blocks approval of this action. */
    blocking: z.boolean(),
    validatorVersion: z.string().min(1).max(80),
    checks: z.array(CareValidationCheckSchema).max(50),
})
    .strict();
export const EncounterCareActionSchema = PlanActionSchema.extend({
    actionId: EncounterActionIdSchema,
    lifecycleStatus: CareActionLifecycleStatusSchema,
    /** Action ids this action depends on; a change to any of them re-opens this one. */
    dependencies: z.array(EncounterActionIdSchema).max(20),
    validation: EncounterActionValidationSchema,
    resolution: EncounterActionResolutionSchema.optional(),
    uncertainties: z.array(UncertaintySchema).max(20),
    sourceSections: z.array(EncounterSourceSectionSchema).max(7),
});
export const EncounterCareBundleSchema = z
    .object({
    schemaVersion: z.literal(ENCOUNTER_CARE_BUNDLE_SCHEMA_VERSION),
    bundleId: EncounterBundleIdSchema,
    bundleVersion: z.number().int().positive(),
    scope: z
        .object({
        tenantId: z.string().min(1).max(100).optional(),
        patientId: z.string().min(1).max(100),
        encounterId: z.string().min(1).max(100),
    })
        .strict(),
    sourceSnapshot: z
        .object({
        contentHash: Sha256Schema,
        contextRef: VisitContextRefSchema.optional(),
        generation: z.number().int().nonnegative().optional(),
    })
        .strict(),
    clinicalSummary: z.string().trim().min(1).max(2_000),
    suggestedAssessment: SuggestedAssessmentSchema,
    suggestedDiagnosisCode: SuggestedDiagnosisCodeSchema,
    actions: z.array(EncounterCareActionSchema).min(1).max(20),
    unresolvedQuestions: z.array(z.string().trim().min(1).max(500)).max(30),
    redFlags: z.array(z.string().trim().min(1).max(500)).max(30),
    validationSummary: z
        .object({
        status: EncounterActionValidationStatusSchema,
        blockingFailureCount: z.number().int().nonnegative(),
        warningCount: z.number().int().nonnegative(),
        validatedAt: IsoDateTimeSchema,
    })
        .strict(),
    generationTrace: z
        .object({
        engine: CopilotEngineSchema,
        validatorVersion: z.string().min(1).max(80),
    })
        .strict(),
    /** Canonical hash of everything above; recomputed by the server, never trusted from a client. */
    contentHash: Sha256Schema,
    createdAt: IsoDateTimeSchema,
})
    .strict();
//# sourceMappingURL=encounter-care-bundle.js.map